import {
  useInfiniteQuery,
  InfiniteData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"
import update, { Spec } from "immutability-helper"
import api from "services/api"
import { useAuth } from "providers/auth"

export interface IUser {
  id: string
  name: string
  email: string
  username: string
  avatar_thumb: string
}

export interface IMessage {
  id: string
  body: string
  created_at: string
  updated_at?: string
  is_unread?: boolean
  creator: IUser
  reply_to?: IMessage
}

type IType = "group" | "pair" | "public_channel" | "private_channel"

export interface IDiscussionInfo {
  id: string
  name?: string
  subject?: string
  type: IType
  creator: IUser
  participants?: IUser[]
  created_at: string
  total_unread: string
}

export interface IDiscussionSummary extends IDiscussionInfo {
  last_message?: IMessage
}

export interface CreateMessageBody {
  discussion_id: string
  message_id: string
  body: string
  reply_to_msg?: IMessage
}

export interface MarkUnreadBody {
  discussion_id: string
  message_id: string
}

export function useDiscussionInfo(id: string) {
  const queryClient = useQueryClient()
  return useQuery<IDiscussionInfo>(
    ["operation", "administration", "members", id, "discussion"],
    () =>
      api
        .get(`operation/administration/members/${id}/discussion`)
        .then(({ data }) => data.data),
    {
      // staleTime: Infinity,
      refetchOnWindowFocus: false,
      initialData: () =>
        queryClient.getQueryData([
          "suite",
          "communication",
          "discussion",
          "member",
          id,
        ]),
    }
  )
}

interface IResponse extends IPaginatedResponse<IMessage[]> {}

export function useDiscussion(id: string, config?: UseQueryOptions<any>) {
  return useInfiniteQuery<IResponse>(
    ["suite", "communication", "discussion", id, "messages"],
    async ({ pageParam = 1 }) =>
      api
        .get(`suite/communication/discussion/${id}/messages`, {
          params: {
            per_page: 10,
            page: pageParam,
            paginated: true,
          },
        })
        .then(({ data }) => data),
    {
      refetchOnWindowFocus: false,
      refetchInterval: 10000,
      getNextPageParam: ({ meta }) => {
        return meta?.last_page > meta?.current_page && meta?.current_page + 1
      },
      ...config,
    }
  )
}

export function useSendMessage(
  config?: UseMutationOptions<unknown, unknown, CreateMessageBody, unknown>
) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, CreateMessageBody, unknown>(
    ({ discussion_id, body, reply_to_msg }) =>
      api.post(`suite/communication/discussion/${discussion_id}/messages`, {
        body,
        reply_to_id: reply_to_msg?.id,
      }),
    {
      ...config,
      onMutate({ discussion_id, body, reply_to_msg, message_id }) {
        queryClient.cancelQueries(["suite", "communication", "discussion"])
        const previous = queryClient.getQueryData<InfiniteData<IResponse>>([
          "suite",
          "communication",
          "discussion",
          discussion_id,
          "messages",
        ])

        const newMessage: IMessage = {
          id: message_id,
          body,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          creator: user as IUser,
          reply_to: reply_to_msg,
        }
        const newData = update(previous, {
          pages: {
            0: {
              data: {
                $splice: [[0, 0, newMessage]],
              },
            },
          },
        } as Spec<InfiniteData<IResponse> | undefined>)

        queryClient.setQueryData(
          ["suite", "communication", "discussion", discussion_id, "messages"],
          newData
        )

        return newMessage
      },
      onSettled(data, err, vars, ctx) {
        config?.onSuccess?.(data, vars, ctx)
        queryClient.invalidateQueries(
          ["suite", "communication", "discussion"],
          { exact: true }
        )
        queryClient.invalidateQueries(
          [
            "suite",
            "communication",
            "discussion",
            vars.discussion_id,
            "messages",
          ],
          { exact: true }
        )
      },
    }
  )
}

// It still needs a lot of improvements
export function useReSendMessage(
  config?: UseMutationOptions<unknown, unknown, CreateMessageBody, unknown>
) {
  const queryClient = useQueryClient()

  return useMutation<unknown, unknown, CreateMessageBody, unknown>(
    ({ discussion_id, body }) =>
      api.post(`suite/communication/discussion/${discussion_id}/messages`, {
        body,
      }),
    {
      ...config,
      onSuccess(data, vars, conext) {
        config?.onSuccess?.(data, vars, conext)
        queryClient.invalidateQueries(["suite", "communication", "discussion"])
      },
    }
  )
}

export function useMarkUnread(
  config?: UseMutationOptions<unknown, unknown, MarkUnreadBody, unknown>
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, MarkUnreadBody, unknown>(
    ({ discussion_id, message_id }) =>
      new Promise((res) => setTimeout(() => res(true), Math.random() * 1500)),
    //api.post(`discussion/${discussion_id}/messages/${message_id}`),
    {
      ...config,
      onMutate({ discussion_id, message_id }) {
        queryClient.cancelQueries(["suite", "communication", "discussion"])
        const previous = queryClient.getQueryData<InfiniteData<IResponse>>([
          "suite",
          "communication",
          "discussion",
          discussion_id,
          "messages",
        ])

        const pages = JSON.parse(
          JSON.stringify(
            previous?.pages.map((page) => {
              const filtredData = page.data.map((message) => {
                return message.id === message_id
                  ? { ...message, is_unread: true }
                  : { ...message, is_unread: false }
              })
              return { ...page, data: filtredData }
            })
          )
        )
        const newData = { ...previous, pages }

        queryClient.setQueryData(
          ["suite", "communication", "discussion", discussion_id, "messages"],
          newData
        )
      },
      onSuccess(data, vars, ctx) {
        config?.onSuccess?.(data, vars, ctx)
        // queryClient.invalidateQueries(
        //   ["discussion", vars.discussion_id, "messages"],
        //   { exact: true }
        // )
      },
    }
  )
}
